import { CallTranscriptionType } from "@/types/voice/call_transcription";

export type GroupedSegment = {
    texts: string[];
    channel: number;
    startTime: number;
    endTime: number;
    speaker?: string;
    speakerIndex?: number;
};

function speakerIndex(speaker: string, speakers: Map<string, number>): number {
    const known = speakers.get(speaker);
    if (known !== undefined) return known;
    const index = speakers.size;
    speakers.set(speaker, index);
    return index;
}

function namedSpeaker(channel: unknown, speaker: string | null | undefined): string | undefined {
    if (channel == null || typeof channel === "number") return undefined;
    const name = speaker?.trim();
    return name || undefined;
}

export function groupSegments(segments: CallTranscriptionType["segments"]): GroupedSegment[] {
    if (!segments?.length) return [];

    const speakers = new Map<string, number>();
    return segments.reduce((groups: GroupedSegment[], segment) => {
        const speaker = namedSpeaker(segment.channel, (segment as { speaker?: string | null }).speaker);
        const channel = typeof segment.channel === "number" ? segment.channel : 0;
        const currentGroup = groups[groups.length - 1];

        if (currentGroup && currentGroup.channel === channel && currentGroup.speaker === speaker) {
            currentGroup.texts.push(segment.text);
            currentGroup.endTime = segment.end;
        } else {
            groups.push({
                texts: [segment.text],
                channel,
                startTime: segment.start,
                endTime: segment.end,
                speaker,
                speakerIndex: speaker ? speakerIndex(speaker, speakers) : undefined
            });
        }

        return groups;
    }, []);
}

export function groupConversation(transcription: CallTranscriptionType): GroupedSegment[] {
    const segments = transcription.segments ?? [];
    // Named lines (a jury) stay one bubble each. Merging them, or using the
    // pre-grouped turns, sticks the highlight on a block that outlasts the video.
    if (segments.some(segment => namedSpeaker(segment.channel, (segment as { speaker?: string | null }).speaker))) {
        const speakers = new Map<string, number>();
        return segments.map(segment => {
            const speaker = namedSpeaker(segment.channel, (segment as { speaker?: string | null }).speaker);
            return {
                texts: [segment.text],
                channel: typeof segment.channel === "number" ? segment.channel : 0,
                startTime: segment.start,
                endTime: segment.end,
                speaker,
                speakerIndex: speaker ? speakerIndex(speaker, speakers) : undefined
            };
        });
    }

    const turns = transcription.turns?.items;
    if (turns?.length) {
        const speakers = new Map<string, number>();
        return turns.map(turn => {
            const speaker = namedSpeaker(turn.channel, turn.speaker);
            let channel = typeof turn.channel === "number" ? turn.channel : null;
            if (channel == null) {
                const label = turn.speaker?.trim().toLowerCase() ?? "";
                if (label === "agent") channel = 1;
                else if (label) channel = 2;
                else channel = 0;
            }

            return {
                texts: [turn.text],
                channel: channel ?? 0,
                startTime: turn.start,
                endTime: turn.end,
                speaker,
                speakerIndex: speaker ? speakerIndex(speaker, speakers) : undefined
            };
        });
    }

    return groupSegments(transcription.segments || []);
}
